/**
 * WorkflowPhotoCaptureModal - Two-step modal for per-checklist-item
 * photo capture inside the work order workflow.
 *
 * Step 1: PhotoCapture (camera + file picker + drop zone). Once the
 *         user captures or picks an image, we move to step 2 keeping
 *         the captured frame in state.
 * Step 2: Preview the captured image as a thumbnail with two free-text
 *         inputs:
 *           - Area    short label of where the photo was taken
 *                     (e.g. "Entry", "Second Floor")
 *           - Details longer description of what's in the frame
 *         The user can either save (uploads + closes) or "Retake" to
 *         go back to step 1.
 *
 * The modal is content-only — owns no upload-side state itself
 * beyond the in-flight isSaving flag. The parent supplies a single
 * `onSave(payload)` callback that wraps the upload, so this organism
 * stays reusable for both work-order and inspection contexts.
 *
 * Photo type resolution + the actual API call are the parent's job.
 * Keeps this component focused on the user-facing capture + metadata
 * flow.
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../Modal';
import { PhotoCapture } from '../PhotoCapture';
import type { CapturedPhoto } from '../PhotoCapture';
import { Button } from '../../atoms';
import { areaService } from '../../../services/areaService';
import type { Area } from '../../../services/areaService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * The payload the modal hands to its onSave callback once the user
 * confirms. The parent uploads via uploadService and then handles
 * the post-upload UX (refetch, mark item done, etc.).
 */
export interface WorkflowPhotoCapturePayload {
    photo: CapturedPhoto;
    /** Short label for the photo — shown above the description in the gallery. */
    title: string;
    /** Area name. Comes from the CMS-managed picker when the company has a
     *  vocabulary, or free text when it doesn't. */
    area: string;
    /** The id of the picked CMS area, when one was chosen. Null for free text. */
    areaId: number | null;
    /** Longer description of what the photo shows. */
    details: string;
}

export interface WorkflowPhotoCaptureModalProps {
    /** Whether the modal is currently visible */
    isOpen: boolean;
    /** Close handler — called by Cancel + the Modal's X */
    onClose: () => void;
    /** Save handler — async; the modal awaits it before closing so
     *  the parent can run the upload before the user dismisses. */
    onSave: (payload: WorkflowPhotoCapturePayload) => Promise<void>;
    /** Title shown in the modal header. Defaults to "Add photo".
     *  Parent can override with the item title for context, e.g.
     *  "Photo for: Vacuum lobby". */
    title?: string;
    /** Optional outer className merged onto the root (Modal) element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const inputClass =
    'block w-full rounded-md border border-brand-border px-12px py-10px text-16px font-sans focus:outline-none focus:ring-2 focus:ring-brand-primary';

export const WorkflowPhotoCaptureModal: React.FC<WorkflowPhotoCaptureModalProps> = ({
    isOpen,
    onClose,
    onSave,
    title = 'Add photo',
    className,
}) => {
    /* Captured frame holds the user's chosen image. Null = step 1
     * (PhotoCapture surface); set = step 2 (preview + metadata). */
    const [captured, setCaptured] = useState<CapturedPhoto | null>(null);
    const [photoTitle, setPhotoTitle] = useState('');
    const [area, setArea] = useState('');
    const [areaId, setAreaId] = useState<number | null>(null);
    const [details, setDetails] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* CMS-managed areas — one shared list for every customer. Loaded once the
     * modal opens so the request doesn't fire on every page that merely
     * renders the modal closed. An empty list is a valid state — the Area
     * field then falls back to free text rather than blocking capture. */
    const [areas, setAreas] = useState<Area[]>([]);
    useEffect(() => {
        if (!isOpen) {
            setAreas([]);
            return;
        }
        let cancelled = false;
        areaService.listActive().then((rows) => {
            if (!cancelled) setAreas(rows);
        });
        return () => { cancelled = true; };
    }, [isOpen]);

    /** Reset all local state. Called on close so re-opening starts
     *  fresh on step 1 with empty inputs. */
    const reset = () => {
        setCaptured(null);
        setPhotoTitle('');
        setArea('');
        setAreaId(null);
        setDetails('');
        setError(null);
        setIsSaving(false);
    };

    /** Cancel handler used by both X and the Cancel button. */
    const handleClose = () => {
        if (isSaving) return; // disallow close while upload in flight
        reset();
        onClose();
    };

    /** Step 2 → step 1: discard the captured image, return to picker. */
    const handleRetake = () => {
        setCaptured(null);
        setError(null);
    };

    /** Step 2 confirm: hand the payload to the parent, await upload,
     *  then close on success. Errors stay in the modal so the user
     *  can retry without losing the capture. */
    const handleSave = async () => {
        if (!captured) return;
        setIsSaving(true);
        setError(null);
        try {
            await onSave({
                photo: captured,
                title: photoTitle.trim(),
                area: area.trim(),
                areaId,
                details: details.trim(),
            });
            reset();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save photo');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        /* The root rendered element is <Modal>, which owns the actual DOM
         * root, so both structural markers are forwarded onto it: the BEM
         * block class through className, and this organism's own data-ref
         * through Modal's dataRef override (which defaults to
         * "organism-modal" when a caller doesn't claim it). */
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            size="md"
            className={['workflow-photo-capture-modal', className ?? ''].join(' ')}
            dataRef="organism-workflow-photo-capture-modal"
        >
            {!captured ? (
                /* Step 1: capture the image */
                <div className="workflow-photo-capture-modal__capture">
                    <p className="text-13px text-brand-text-muted mb-12px">
                        Use the camera, pick a file, or drag and drop.
                    </p>
                    <PhotoCapture
                        onCapture={(photo) => setCaptured(photo)}
                        label="Take Photo"
                    />
                </div>
            ) : (
                /* Step 2: preview + area/details + save */
                <div className="workflow-photo-capture-modal__preview space-y-12px">
                    {/* Thumbnail of the captured image. Clicking Retake
                     *  drops back to step 1 so the user can replace it. */}
                    <div className="relative">
                        <img
                            src={captured.base64}
                            alt="Captured"
                            className="w-full max-h-[280px] object-contain rounded-md border border-brand-border bg-brand-surface"
                        />
                        <button
                            type="button"
                            onClick={handleRetake}
                            disabled={isSaving}
                            className="absolute top-8px right-8px inline-flex items-center px-10px py-4px min-h-44px desktop:min-h-32px text-12px font-sans font-medium bg-white/90 text-brand-text border border-brand-border rounded-md hover:bg-white"
                        >
                            Retake
                        </button>
                    </div>

                    {/* Short title — the label shown above the description in
                     *  the submitted gallery and the PDF. */}
                    <div>
                        <label htmlFor="photo-title" className="block text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wide mb-4px">
                            Title
                        </label>
                        <input
                            id="photo-title"
                            type="text"
                            value={photoTitle}
                            onChange={(e) => setPhotoTitle(e.target.value)}
                            disabled={isSaving}
                            placeholder='e.g. "Scuffed baseboard"'
                            className={inputClass}
                            maxLength={200}
                        />
                    </div>

                    {/* Area — a picker when the company has a CMS vocabulary,
                     *  free text when it doesn't. Picking beats typing here:
                     *  free text made per-area reporting impossible because
                     *  "Mens Restroom" and "MENS RR" are different strings. */}
                    <div>
                        <label htmlFor="photo-area" className="block text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wide mb-4px">
                            Area
                        </label>
                        {areas.length > 0 ? (
                            <select
                                id="photo-area"
                                value={areaId == null ? '' : String(areaId)}
                                onChange={(e) => {
                                    const nextId = e.target.value ? Number(e.target.value) : null;
                                    setAreaId(nextId);
                                    /* Keep the name alongside the id — the photo
                                       record stores both so a later CMS rename
                                       doesn't rewrite history. */
                                    setArea(areas.find((a) => a.id === nextId)?.name ?? '');
                                }}
                                disabled={isSaving}
                                className={`${inputClass} bg-white`}
                            >
                                <option value="">— Select an area —</option>
                                {areas.map((a) => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                id="photo-area"
                                type="text"
                                value={area}
                                onChange={(e) => setArea(e.target.value)}
                                disabled={isSaving}
                                placeholder='e.g. "Entry", "Second Floor"'
                                className={inputClass}
                                maxLength={100}
                            />
                        )}
                    </div>

                    {/* Free-text Details */}
                    <div>
                        <label htmlFor="photo-details" className="block text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wide mb-4px">
                            Details
                        </label>
                        <textarea
                            id="photo-details"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            disabled={isSaving}
                            rows={3}
                            placeholder="Describe what's in the photo or where it was taken"
                            className={`${inputClass} resize-none`}
                            maxLength={500}
                        />
                    </div>

                    {error && (
                        <div className="text-12px text-red-700 bg-red-50 border border-red-200 rounded-md px-10px py-6px">
                            {error}
                        </div>
                    )}

                    {/* Action row */}
                    <div className="flex justify-end gap-8px pt-4px">
                        <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
                            Save Photo
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default WorkflowPhotoCaptureModal;
